import { Button, Form, Input, Popconfirm, Skeleton, message } from "antd";
import moment from "moment";
import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import GroupBadge, { isGroupRow } from "./GroupBadge";

const { TextArea } = Input;

/**
 * Resolve a display name off a populated User.
 *
 * The document carries one of student/teacher/parent/admin depending on role, so
 * pick whichever is present rather than assuming a shape.
 */
function displayName(user) {
  const profile =
    user?.teacher || user?.student || user?.parent || user?.admin || null;
  if (profile?.first_name || profile?.last_name) {
    return `${profile.first_name ?? ""} ${profile.last_name ?? ""}`.trim();
  }
  return user?.username || user?.email || "School";
}

/**
 * "Discussions" tab.
 *
 * Threads are admin-authored (the create form is gated on `canEdit`), but replies
 * are open to every role that can see the board — that is what keeps the board
 * two-way while still honouring "only admins post".
 */
function BoardDiscussions({ api, canEdit }) {
  const queryClient = useQueryClient();
  const [threadForm] = Form.useForm();
  const [replyDrafts, setReplyDrafts] = useState({});

  const threads = useQuery(api.keys.threads, api.fetchThreads, {
    enabled: api.enabled,
  });

  // A group thread is read-only on a branch board: only the Group Console can
  // delete it. In group scope every thread is the group's own.
  const rowEditable = (record) =>
    canEdit && (api.scope === "group" || !isGroupRow(record));

  const invalidate = () => {
    queryClient.invalidateQueries(api.keys.threads);
    queryClient.invalidateQueries(api.keys.board);
  };

  const onError = (err) => {
    message.error(
      err?.response?.data?.error?.errors?.[0] || "Something went wrong",
    );
  };

  const threadMutation = useMutation(
    (values) => api.createThread({ details: values.details }),
    {
      onSuccess: () => {
        message.success("Posted");
        threadForm.resetFields();
        invalidate();
      },
      onError,
    },
  );

  const replyMutation = useMutation(
    ({ threadId, comment }) => api.addComment(threadId, comment),
    {
      onSuccess: (_data, variables) => {
        setReplyDrafts((prev) => ({ ...prev, [variables.threadId]: "" }));
        invalidate();
      },
      onError,
    },
  );

  const deleteThreadMutation = useMutation(api.deleteThread, {
    onSuccess: () => {
      message.success("Discussion deleted");
      invalidate();
    },
    onError,
  });

  const deleteCommentMutation = useMutation(api.deleteComment, {
    onSuccess: () => {
      message.success("Reply removed");
      invalidate();
    },
    onError,
  });

  const submitReply = (threadId) => {
    const comment = (replyDrafts[threadId] ?? "").trim();
    if (!comment) return;
    replyMutation.mutate({ threadId, comment });
  };

  return (
    <div className="pt-8 space-y-6">
      {canEdit && (
        <div className="bg-white p-6 rounded-xl">
          <div style={{ color: "#E46E31" }} className="text-2xl font-semibold pb-4">
            Start a Discussion
          </div>
          <Form
            form={threadForm}
            layout="vertical"
            onFinish={threadMutation.mutate}
          >
            <Form.Item
              name="details"
              rules={[{ required: true, message: "Please write something" }]}
            >
              <TextArea
                rows={3}
                className="resize-none"
                maxLength={5000}
                placeholder="Share an update with this class's families."
              />
            </Form.Item>
            <div className="flex justify-end">
              <Button
                type="primary"
                size="large"
                htmlType="submit"
                loading={threadMutation.isLoading}
              >
                Post
              </Button>
            </div>
          </Form>
        </div>
      )}

      <div className="bg-white p-6 rounded-xl">
        <div style={{ color: "#E46E31" }} className="text-2xl font-semibold pb-4">
          Discussions
        </div>

        {threads.isLoading ? (
          <Skeleton active />
        ) : threads.data?.length ? (
          <div className="space-y-6">
            {threads.data.map((thread) => (
              <div key={thread._id} className="border rounded-xl p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-semibold capitalize inline-flex items-center gap-2">
                      {displayName(thread.user)}
                      {api.scope === "class" && isGroupRow(thread) && (
                        <GroupBadge compact />
                      )}
                    </div>
                    <div className="text-xs text-gray-400">
                      {moment(thread.createdAt).format("MMMM Do YYYY, h:mm a")}
                    </div>
                  </div>
                  {rowEditable(thread) && (
                    <Popconfirm
                      title="Delete this discussion and all its replies?"
                      onConfirm={() => deleteThreadMutation.mutate(thread._id)}
                      okText="Yes"
                      cancelText="No"
                    >
                      <Button danger size="small">
                        Delete
                      </Button>
                    </Popconfirm>
                  )}
                </div>

                <div
                  style={{ whiteSpace: "pre-wrap" }}
                  className="text-base py-3"
                >
                  {thread.details}
                </div>

                {!!thread.comments?.length && (
                  <div className="pl-4 border-l space-y-3 mt-2">
                    {thread.comments.map((comment) => (
                      <div
                        key={comment._id}
                        className="flex justify-between items-start"
                      >
                        <div>
                          <div className="text-sm font-semibold capitalize">
                            {displayName(comment.user)}
                          </div>
                          <div className="text-sm">{comment.comment}</div>
                          <div className="text-xs text-gray-400">
                            {moment(comment.createdAt).fromNow()}
                          </div>
                        </div>
                        {canEdit && api.deleteComment && (
                          <Popconfirm
                            title="Remove this reply?"
                            onConfirm={() =>
                              deleteCommentMutation.mutate(comment._id)
                            }
                            okText="Yes"
                            cancelText="No"
                          >
                            <Button danger size="small" type="text">
                              Remove
                            </Button>
                          </Popconfirm>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Replies are open to every viewer role, not just admins —
                    but the Group Console only reads them, so the composer is
                    hidden there. */}
                {api.addComment && (
                <div className="flex gap-2 pt-4">
                  <Input
                    placeholder="Write a reply..."
                    value={replyDrafts[thread._id] ?? ""}
                    maxLength={2000}
                    onChange={(e) =>
                      setReplyDrafts((prev) => ({
                        ...prev,
                        [thread._id]: e.target.value,
                      }))
                    }
                    onPressEnter={() => submitReply(thread._id)}
                  />
                  <Button
                    type="primary"
                    onClick={() => submitReply(thread._id)}
                    loading={
                      replyMutation.isLoading &&
                      replyMutation.variables?.threadId === thread._id
                    }
                  >
                    Reply
                  </Button>
                </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center text-gray-400 py-10">
            {api.scope === "group"
              ? "No group discussions yet. A thread you start here appears on every Junior branch."
              : "No discussions have been started for this class yet."}
          </div>
        )}
      </div>
    </div>
  );
}

export default BoardDiscussions;
